/**
 * ExemplarOutput — renders the "Ideal Candidate Research" exemplar data: the
 * archetype summary, several chip/bullet attribute clusters, and a Sources
 * section rendering each citation as a real outbound link. Used by ExemplarPanel.
 */

import { Box, Link, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, ChipSection, SectionLabel } from "./shared";

export default function ExemplarOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  const sources: any[] = o.sources ?? [];

  return (
    <Stack spacing={2.5}>
      {o.archetype_summary && (
        <Typography variant="body1" color="text.primary">
          {o.archetype_summary}
        </Typography>
      )}

      <ChipSection label={t("ExemplarOutput.signatureSkills")} items={o.signature_skills} />
      <ChipSection label={t("ExemplarOutput.recurringKeywords")} items={o.recurring_keywords} variant="outline" />

      <BulletSection label={t("ExemplarOutput.typicalPriorRoles")} items={o.typical_prior_roles} />
      <BulletSection label={t("ExemplarOutput.commonEmployers")} items={o.common_employers} />
      <BulletSection label={t("ExemplarOutput.credentialsAndSignals")} items={o.credentials_and_signals} />
      <BulletSection label={t("ExemplarOutput.differentiators")} items={o.differentiators} />

      {sources.length > 0 && (
        <Box>
          <SectionLabel>{t("ExemplarOutput.sources")}</SectionLabel>
          <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 0.75, pl: 0, listStyle: "none" }}>
            {sources.map((s, i) => {
              const title = s?.title ?? s?.url ?? t("ExemplarOutput.sourceFallback", { index: i + 1 });
              const url = s?.url;
              return (
                <Box component="li" key={`${url ?? title}-${i}`}>
                  {url ? (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      }}
                    >
                      {title}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {title}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
